import { useEffect, useState } from "react";
import { Settings as SettingsIcon, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import InfoTip from "../components/InfoTip";

export default function Settings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/admin/settings").then((r) => setForm(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!form) return <Skeleton className="h-96 w-full" />;

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const setNum = (k) => (e) => setForm({ ...form, [k]: Number(e.target.value) });

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/admin/settings", form);
      toast.success("Parametres enregistres.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-2">
          <SettingsIcon className="h-7 w-7 text-[#3A75C4]" /> Parametres
        </h1>
        <p className="text-sm text-muted-foreground">Configuration generale et politique de securite d'ALARA.</p>
      </div>

      <Card data-testid="settings-general">
        <CardHeader><CardTitle className="text-base font-heading">Identite de l'application</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Nom de l'application</Label>
            <Input value={form.app_name || ""} onChange={set("app_name")} data-testid="settings-app-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Institution</Label>
            <Input value={form.institution || ""} onChange={set("institution")} data-testid="settings-institution" />
          </div>
        </CardContent>
      </Card>

      <Card data-testid="settings-security">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            Politique de securite
            <InfoTip testid="tip-lockout" text="Apres le nombre maximal de tentatives echouees, le compte est verrouille pendant la duree de verrouillage." />
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Duree de session (minutes)</Label>
            <Input type="number" min={5} value={form.session_duration_minutes} onChange={setNum("session_duration_minutes")} data-testid="settings-session" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tentatives de connexion max</Label>
            <Input type="number" min={3} value={form.max_login_attempts} onChange={setNum("max_login_attempts")} data-testid="settings-attempts" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Duree de verrouillage (minutes)</Label>
            <Input type="number" min={1} value={form.lockout_minutes} onChange={setNum("lockout_minutes")} data-testid="settings-lockout" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Longueur minimale mot de passe</Label>
            <Input type="number" min={8} value={form.password_min_length} onChange={setNum("password_min_length")} data-testid="settings-pwdlen" />
          </div>
        </CardContent>
      </Card>

      <Card data-testid="settings-maintenance">
        <CardHeader><CardTitle className="text-base font-heading">Mode maintenance</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <p className="text-sm font-medium">Application en maintenance</p>
              <p className="text-xs text-muted-foreground">Seuls les administrateurs autorises conservent l'acces.</p>
            </div>
            <Switch checked={!!form.maintenance_mode} onCheckedChange={(v) => setForm({ ...form, maintenance_mode: v })} data-testid="settings-maintenance-toggle" />
          </div>
          {form.maintenance_mode && (
            <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              Le mode maintenance sera actif des l'enregistrement.
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Message de maintenance</Label>
            <Input value={form.maintenance_message || ""} onChange={set("maintenance_message")} data-testid="settings-maintenance-message" />
          </div>
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="settings-save">
        <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement..." : "Enregistrer les parametres"}
      </Button>
    </div>
  );
}
